import React, { useEffect, useState } from "react";
import * as S from "../../style/StyleComponentSection";
import * as U from "../../style/StyleComponentUtility";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/Store";
import { select_reservation_detail } from "../../Axios";
import { commaPrice, get_day_timestamp } from "../../Validation";
import theme from "../../style/theme";
import Pagination from "../Utility/Pagination";
import { useNavigate } from "react-router-dom";
import arrowIcon from "../../assets/icons/arrow_forward_ios.svg";
import { CSVLink } from "react-csv";

const lightGray02     = theme.colors.lightGray02;
const middleGray02     = theme.colors.middleGray02;
const pointYellow  = theme.colors.pointYellow;
const mainBlack = theme.colors.mainBlack

interface ReservationList {
    construction_reservation_ID                : number | string;
    construction_reservation_progress          : number;
    construction_reservation_progress_name     : string;
    construction_reservation_division          : number;
    construction_reservation_division_name     : string;
    construction_reservation_name              : string;
    construction_reservation_big_area          : string;
    construction_reservation_small_area        : string;
    construction_reservation_before_big_area   : string;
    construction_reservation_before_small_area : string;
    construction_reservation_reservationTime   : any;
    construction_reservation_time_format       : string;
    construction_reservation_time_name         : string;
    construction_reservation_schedule_status_name      : string;
    construction_reservation_installation_date_format  : string;
    construction_reservation_installation_time_period  : string;
    construction_reservation_installation_display      ?: string;
    construction_reservation_requests          : string;
    construction_reservation_point             : number;
    brand_kor_name                             : string;
    product_name                               : string;
    color_name                                 : string;
    construction_reservation_count             : number;
    construction_reservation_address           : string;
    construction_reservation_before_address    : string;
    construction_reservation_phone_number      : string;
    construction_reservation_pay               : number;
    construction_reservation_high_status_name  : string;
    construction_reservation_square_meter      : number;
    construction_reservation_square_meter_name : string;
    construction_reservation_space             : number;
    construction_reservation_space_name        : string;
};

const excel_data_headers = [
    {label: '예약번호', key: 'construction_reservation_ID'},
    {label: '진행상태', key: 'construction_reservation_progress_name'},
    {label: '구분', key: 'construction_reservation_division_name'},
    {label: '고객명', key: 'construction_reservation_name'},
    {label: '전화번호', key: 'construction_reservation_phone_number'},
    {label: '브랜드', key: 'brand_kor_name'},
    {label: '제품명', key: 'product_name'},
    {label: '색상', key: 'color_name'},
    {label: '수량', key: 'construction_reservation_count'},
    {label: '지역', key: 'construction_reservation_big_area'},
    {label: '구/군', key: 'construction_reservation_small_area'},
    {label: '주소', key: 'construction_reservation_address'},
    {label: '천장높이', key: 'construction_reservation_high_status_name'},
    {label: '전체 평수', key: 'construction_reservation_square_meter_name'},
    {label: '설치장소', key: 'construction_reservation_space_name'},
    {label: '시공희망일', key: `construction_reservation_time_format`},
    {label: '시간', key: `construction_reservation_time_name`},
    {label: '해피콜 여부', key: `construction_reservation_schedule_status_name`},
    {label: '확정 시공일', key: `construction_reservation_installation_display`},
    {label: '시공액', key: `construction_reservation_pay`},
    {label: 'AP 사용 내역', key: `construction_reservation_point`},
    {label: '요청사항', key: `construction_reservation_requests`},
]

interface ProgressList {
    construction_reservation_progress : number;
    progress_count : number;
};

interface ProgressListCount {
    matching : number;
    waiting  : number;
    complete_payment : number;
    complete_construct : number;
    cancel : number;
}

const ReservationDetail: React.FC = () => {
    const navigate = useNavigate();
    const customerData = useSelector((state: RootState) => state.customerData);
    const [reservationList, setReservationList] = useState<ReservationList[]>([]);
    const [progressListCount, setProgressListCount] = useState<ProgressListCount>();
    const [progressTotal, setProgressTotal] = useState<number>(0);
    const [csvList, setCsvList] = useState<any[]>([]);

    const [reservationProgress, setReservationProgress] = useState<number>(0); // 진행결과
    const [page, setPage] = useState<number>(1); // 현재 페이지
    const [listCount, setListCount] = useState<number>(10); // 한 페이지에 리스트 몇 개까지 보여줄지
    const [totalReservation, setTotalReservation] = useState<number>(0); // 해당 table 전체 개수
    const [field, setField] = useState<number>(0); // 정렬하는 필드 0 : PK
    const [listOrder, setListOrder] = useState<number>(0); // 상하행 정렬 0 : DESC, 1 : ASC

    // props에서 page 변경하면 그 페이지 불러오는 함수
    useEffect(() => {
        if(!page) return;
        select_reservation_detail(
            customerData.customer_ID,
            reservationProgress,
            listCount,
            page,
            field,
            listOrder
        ).then(result => {
            let total = 0;
            let count_by_progress:ProgressListCount = {
                matching : 0,
                waiting  : 0,
                complete_payment : 0,
                complete_construct : 0,
                cancel : 0
            };
            result.progress_count.forEach((element:ProgressList) => {
                total += element.progress_count;
                switch (element.construction_reservation_progress) {
                    case 1 : count_by_progress.matching = element.progress_count; break;
                    case 2 : count_by_progress.waiting = element.progress_count; break;
                    case 3 : count_by_progress.complete_payment = element.progress_count; break;
                    case 4 : count_by_progress.complete_construct = element.progress_count; break;
                    case 5 : count_by_progress.cancel = element.progress_count; break;
                };
            });
            setProgressTotal(total);
            setProgressListCount(count_by_progress);
            setReservationList(result.rows);
            setTotalReservation(result.total_count[0]["reservation_total"]);
        });
    }, [customerData.customer_ID, listCount, listOrder, page, reservationProgress, field]);

    // Pagination 컴포넌트에서 페이지 선택하는 함수
    const handlePage = (page: number) => {
        setPage(page);
    };

    // 진행상황별로 보기
    const handleSelectProgress = (progress_num: number) => {
        setReservationProgress(progress_num);
        setPage(1);
        setField(0);
        setListOrder(0);
    };

    const handleSort = (field_num: number) => {
        if(field !== field_num) {
            setField(field_num);
            setListOrder(1);
        } else {
            if(listOrder === 1) {
                setListOrder(0);
            } else if (listOrder === 0) {
                setField(0)
            }
        };
        setPage(1) // 정렬시 1페이지로 이동 => 삭제해도 무방
    };

    const payStatusBackground = (status: number) => {
        switch (status) {
            case 1 : return lightGray02; // 매칭 중
            case 2 : return middleGray02; // 결제 대기
            case 3 : return pointYellow; // 결제 완료
            case 4 : return mainBlack
            default : return "";
        }
    };

    const payStatusColor = (status: number) => {
        switch (status) {
            case 1 : return mainBlack;
            case 2 : return mainBlack;
            case 3 : return mainBlack;
            case 4 : return "#fff";
            default : return mainBlack;
        }
    };

    const sumReservationList = () => {
        let arr: any[] = reservationList.map(item => ({
            ...item,
            construction_reservation_installation_display: item.construction_reservation_installation_date_format
                ? `${item.construction_reservation_installation_date_format} ${item.construction_reservation_installation_time_period}`
                : "-",
        }));
        let obj = {
            construction_reservation_ID: '',
            construction_reservation_progress: 0,
            construction_reservation_progress_name: '',
            construction_reservation_division: 0,
            construction_reservation_division_name: '',
            construction_reservation_name: '',
            construction_reservation_big_area: '',
            construction_reservation_small_area: '',
            construction_reservation_before_big_area: '',
            construction_reservation_before_small_area: '',
            construction_reservation_reservationTime: '',
            construction_reservation_time_format: '',
            construction_reservation_time_name: '',
            construction_reservation_schedule_status_name: '',
            construction_reservation_installation_date_format: '',
            construction_reservation_installation_time_period: '',
            construction_reservation_installation_display: '',
            construction_reservation_requests: '',
            construction_reservation_point: 0,
            brand_kor_name: '',
            product_name: '',
            color_name: '',
            construction_reservation_count: 0,
            construction_reservation_address: '',
            construction_reservation_before_address: '',
            construction_reservation_phone_number: '',
            construction_reservation_pay: 0,
            construction_reservation_high_status_name: '',
            construction_reservation_square_meter: 0,
            construction_reservation_square_meter_name: '',
            construction_reservation_space: 0,
            construction_reservation_space_name: '',
        };
        for (let i = 0; i < arr.length; i++) {
            obj.construction_reservation_count += arr[i].construction_reservation_count;
            obj.construction_reservation_pay += arr[i].construction_reservation_pay;
            obj.construction_reservation_point += arr[i].construction_reservation_point;
        };
        obj.construction_reservation_ID = `총계: ${arr.length}`;
        arr.push(obj);
        setCsvList(arr);
    };

    // 시공내역 한개 보기 모달시 배경 스크롤 막기
    // useEffect(() => {
    //     if(modal) {
    //         document.body.classList.add('on-modal-back');
    //     } else {
    //         document.body.classList.remove('on-modal-back');
    //     }
    // }, [modal]);
    
    return (
        <S.SectionContainer>
            <S.SectionWidth $width={900}>
                <S.SectionTitleBox>
                    <h1>A.Black 서비스 이용 내역</h1>
                    <h3>예약 내역과 진행 현황을 확인할 수 있습니다.</h3>
                </S.SectionTitleBox>

                <U.BreakLine $height={60} />

                <S.ReservationSummary>
                    <div className="usage-items" onClick={() => handleSelectProgress(0)}>
                        <p>전체</p>
                        <span>{progressTotal}</span>
                    </div>
                    <div className="usage-items" onClick={() => handleSelectProgress(1)}>
                        <p>매칭중</p>
                        <span>{progressListCount?.matching}</span>
                    </div>
                    <div className="usage-items" onClick={() => handleSelectProgress(2)}>
                        <p>결제대기</p>
                        <span>{progressListCount?.waiting}</span>
                    </div>
                    <div className="usage-items" onClick={() => handleSelectProgress(3)}>
                        <p>결제완료</p>
                        <span>{progressListCount?.complete_payment}</span>
                    </div>
                    <div className="usage-items" onClick={() => handleSelectProgress(4)}>
                        <p>시공완료</p>
                        <span>{progressListCount?.complete_construct}</span>
                    </div>
                    <div className="usage-items" onClick={() => handleSelectProgress(5)}>
                        <p>취소</p>
                        <span>{progressListCount?.cancel}</span>
                    </div>
                </S.ReservationSummary>

                <div className="flex justify-between mt-5 mb-3">
                    <div>
                        <h1 className="font-semibold">예약 상세 내역</h1>
                    </div>
                    {/* 페이지당 게시물 수 */}
                    <div>
                        <select
                            className="outline-none cursor-pointer"
                            value={listCount}
                            onChange={e => {
                                setListCount(Number(e.target.value));
                                setPage(1);
                            }}
                        >
                            <option value={10}>10개</option>
                            <option value={20}>20개</option>
                            <option value={30}>30개</option>
                            <option value={50}>50개</option>
                            <option value={100}>100개</option>
                        </select>
                    </div>
                </div>

                <S.ReservationTableHead>
                    <div className="sortable-box w-20" onClick={() => handleSort(1)}>
                        <span>
                            진행상태
                            <img src={arrowIcon} className={`${field === 1 ? `block ${listOrder === 0 ? "rotate-90" : "rotate-[270deg]"}` : "hidden"}`} alt="" />
                        </span>
                    </div>
                    <div className="sortable-box w-20" onClick={() => handleSort(9)}>
                        <span>
                            예약번호
                            <img src={arrowIcon} className={`${field === 9 ? `block ${listOrder === 0 ? "rotate-90" : "rotate-[270deg]"}` : "hidden"}`} alt="" />
                        </span>
                    </div>
                    <div className="sortable-box w-20" onClick={() => handleSort(2)}>
                        <span>
                            구분
                            <img src={arrowIcon} className={`${field === 2 ? `block ${listOrder === 0 ? "rotate-90" : "rotate-[270deg]"}` : "hidden"}`} alt="" />
                        </span>
                    </div>
                    <div className="sortable-box w-24" onClick={() => handleSort(3)}>
                        <span>고객명
                            <img src={arrowIcon} className={`${field === 3 ? `block ${listOrder === 0 ? "rotate-90" : "rotate-[270deg]"}` : "hidden"}`} alt="" />
                        </span>
                    </div>
                    <div className="sortable-box w-40" onClick={() => handleSort(4)}>
                        <span>
                            시공주소
                            <img src={arrowIcon} className={`${field === 4 ? `block ${listOrder === 0 ? "rotate-90" : "rotate-[270deg]"}` : "hidden"}`} alt="" />
                        </span>
                    </div>
                    <div className="sortable-box w-24" onClick={() => handleSort(5)}>
                        <span>
                            시공희망일
                            <img src={arrowIcon} className={`${field === 5 ? `block ${listOrder === 0 ? "rotate-90" : "rotate-[270deg]"}` : "hidden"}`} alt="" />
                        </span>
                    </div>
                    <div className="sortable-box w-20" onClick={() => handleSort(6)}>
                        <span>
                            시간
                            <img src={arrowIcon} className={`${field === 6 ? `block ${listOrder === 0 ? "rotate-90" : "rotate-[270deg]"}` : "hidden"}`} alt="" />
                        </span>
                    </div>
                    <div className="md:block hidden w-[23%]" onClick={() => handleSort(7)}>
                        <span>
                            요청사항
                            <img src={arrowIcon} className={`${field === 7 ? `block ${listOrder === 0 ? "rotate-90" : "rotate-[270deg]"}` : "hidden"}`} alt="" />
                        </span>
                    </div>
                    <div className="sortable-box w-32" onClick={() => handleSort(8)}>
                        <span>
                            AP 사용 내역
                            <img src={arrowIcon} className={`${field === 8 ? `block ${listOrder === 0 ? "rotate-90" : "rotate-[270deg]"}` : "hidden"}`} alt="" />
                        </span>
                    </div>
                    <div className="w-20 cursor-default">시공 결과</div>
                </S.ReservationTableHead>

                <div>
                    {reservationList.map((tmp, idx) => 
                        <S.ReservationTableBody $withBtn={false} key={tmp.construction_reservation_ID} onClick={() =>{
                            navigate('/reservation_select_one', {state : {reservation_ID : tmp.construction_reservation_ID}});
                        }}>
                            <div 
                                className="pay-status-box w-20"
                                style={{
                                    backgroundColor : `${payStatusBackground(tmp.construction_reservation_progress)}`,
                                    color : `${payStatusColor(tmp.construction_reservation_progress)}`,
                                    border : `${tmp.construction_reservation_progress === 5 ? "0.5px solid #A1A1AA" : ""}`
                                }}
                            >
                                {tmp.construction_reservation_progress_name}
                            </div>
                            <div className="w-20">{tmp.construction_reservation_ID}</div>
                            <div className="w-20">{tmp.construction_reservation_division_name}</div>
                            <div className="w-24">{tmp.construction_reservation_name}</div>
                            <div className={`w-40 h-[28px] text-ellipsis text-nowrap overflow-hidden ${tmp.construction_reservation_division === 3 ? "" : "leading-[25px]"}`}>
                                <p>{tmp.construction_reservation_big_area + " " + tmp.construction_reservation_small_area}</p>
                                {tmp.construction_reservation_division === 3 && <p className="text-xs">{tmp.construction_reservation_before_big_area + " " + tmp.construction_reservation_before_small_area}</p>}
                            </div>
                            <div className="w-24">{get_day_timestamp(tmp.construction_reservation_reservationTime).year_month_day}</div>
                            <div className="w-20">{tmp.construction_reservation_time_name}</div>
                            <div className="request-box w-[23%] md:block hidden">{tmp.construction_reservation_requests}</div>
                            {tmp.construction_reservation_progress === 5 && tmp.construction_reservation_point !== 0 ?
                            <div className="w-32 line-through">{commaPrice(tmp.construction_reservation_point)}</div>
                            :
                            <div className="w-32">{commaPrice(tmp.construction_reservation_point)}</div>
                            }
                            <div className="w-20">시공결과</div>
                        </S.ReservationTableBody>
                    )}
                </div>

                <Pagination totalCount={totalReservation} handlePage={handlePage} listCount={listCount} page={page} />

                <div className="flex justify-end">
                    <CSVLink
                        data={csvList}
                        filename={`A.Black_서비스_이용내역.csv`}
                        headers={excel_data_headers}
                        className="hover:font-semibold"
                        onClick={() => {
                            if (!window.confirm("현재 페이지를 다운로드 하시겠습니까?")) {
                                return false;
                            } else {
                                sumReservationList();
                                return true;
                            }
                        }}
                    >
                        엑셀 다운로드
                    </CSVLink>
                </div>

                <U.BreakLine $height={60} />

            </S.SectionWidth>
            
            {/* {modal &&
                <S.ReservationModalBack onClick={(e: React.MouseEvent) => { if(e.target === e.currentTarget) {setModal(false)}}}>
                    <S.excelDownloadModalContent>
                        <div className="inner-box">
                            <div className="title">
                                엑셀 다운로드
                            </div>
                            <div className="option-controll-box">
                                <div className="column-text">갯수</div>
                                <div className="select-box">
                                    
                                </div>
                            </div>
                            <div className="button-box">
                                <CSVLink
                                    data={reservationList}
                                    onClick={() => {
                                        if (!window.confirm("파일을 다운로드 하시겠습니까?")) {
                                            return false;
                                        } else {
                                            get_reservation_detail_for_excel(
                                                customerData.customer_ID,
                                            ).then(result => {
                                                if (result.length > 0) {
                                                    return false;
                                                } else {
                                                    alert("다운로드할 데이터가 없습니다.");
                                                    return false;
                                                }
                                            })
                                        }
                                    }}
                                >
                                    다운로드
                                </CSVLink>
                            </div>
                        </div>
                    </S.excelDownloadModalContent>
                </S.ReservationModalBack>
            } */}
        </S.SectionContainer>
    )
};

export default ReservationDetail;